// Panneau Plugins — gestion des plugins par host.
//
// Liste, pour le host sélectionné, les plugins installés (assignés via
// host_plugins) et les plugins installables (catalogue restant). Les actions
// (installer, retirer, changer de canal, mettre à jour les composants)
// passent par le WebSocket `host:command` : le relay écrit en base et le host
// applique au prochain heartbeat (≤30 s) via `plugins_to_install` /
// `pending_action`.

import { createSignal, For, Show, onMount, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { registerPanel } from "@/panel-registry";
import { apiFetch } from "@/shared/api-fetch";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { asWsOutgoing, envelope, isWsEnvelope } from "@/shared/ws-protocol";
import "./plugins.css";

interface HostItem {
  hostId: string;
  hostname?: string;
  displayName?: string;
}

interface InstalledPlugin {
  id: string;
  name: string;
  description?: string;
  channel: string;
  installed_version: string | null;
  enabled: boolean;
  latest_stable: string | null;
  /** Override d'isolation (null = use plugin manifest default). */
  isolation?: "shared" | "process" | null;
}

const ISOLATIONS = ["auto", "shared", "process"] as const;

interface AvailablePlugin {
  id: string;
  name: string;
  description?: string;
  latest_stable: string | null;
}

const CHANNELS = ["stable", "beta", "alpha"] as const;

const PluginsPanel: Component = () => {
  const ws = useWs();
  const confirmDialog = useConfirm();
  const [hosts, setHosts] = createSignal<HostItem[]>([]);
  const [hostId, setHostId] = createSignal("");
  const [hostChannel, setHostChannel] = createSignal("stable");
  const [installed, setInstalled] = createSignal<InstalledPlugin[]>([]);
  const [available, setAvailable] = createSignal<AvailablePlugin[]>([]);
  const [status, setStatus] = createSignal<PanelStatus | undefined>();
  const [busy, setBusy] = createSignal<string | null>(null);
  const [openVersionsFor, setOpenVersionsFor] = createSignal<string | null>(null);
  // P4.1 — pluginId dont le panneau de réglages avancés (⚙) est déplié.
  const [openSettingsFor, setOpenSettingsFor] = createSignal<string | null>(null);
  // P4.2 — pluginIds avec une commande en file d'attente (appliquée au
  // prochain heartbeat). Effacé au prochain refresh de la liste.
  const [queued, setQueued] = createSignal<Set<string>>(new Set());

  function markQueued(pluginId: string): void {
    setQueued((prev) => new Set<string>(prev).add(pluginId));
  }

  const hostLabel = (h: HostItem) => h.displayName || h.hostname || h.hostId;

  async function loadPlugins(hid: string): Promise<void> {
    if (!hid) return;
    setStatus({ text: "Chargement…", level: "info" });
    setQueued(new Set<string>()); // P4.2 — un refresh dissipe les états « en file d'attente »
    try {
      const r = await apiFetch(`/host/${encodeURIComponent(hid)}/plugins`, {
        credentials: "same-origin",
        headers: { accept: "application/json" },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = (await r.json()) as {
        installed?: InstalledPlugin[];
        available?: AvailablePlugin[];
        hostChannel?: string;
      };
      setInstalled(d.installed || []);
      setAvailable(d.available || []);
      setHostChannel(d.hostChannel || "stable");
      setStatus({
        text: `${(d.installed || []).length} installé(s) · ${(d.available || []).length} disponible(s)`,
        level: "info",
      });
    } catch {
      setInstalled([]);
      setAvailable([]);
      setStatus({ text: "Erreur de chargement des plugins", level: "danger" });
    }
  }

  async function loadHosts(): Promise<void> {
    try {
      const r = await apiFetch("/host/list", {
        credentials: "same-origin",
        headers: { accept: "application/json" },
      });
      const d = (await r.json()) as { items?: HostItem[] };
      const items = Array.isArray(d.items) ? d.items : [];
      setHosts(items);
      if (!hostId() && items.length) {
        setHostId(items[0].hostId);
        void loadPlugins(items[0].hostId);
      }
    } catch {
      setStatus({ text: "Erreur de chargement des hôtes", level: "danger" });
    }
  }

  // Envoie une commande `host:command` puis recharge la liste après un court
  // délai (le temps que le relay écrive en base).
  function sendCmd(command: Record<string, unknown>, busyKey: string, label: string): void {
    const hid = hostId();
    if (!hid) return;
    if (!ws.isOpen()) {
      setStatus({ text: "WebSocket non connecté — impossible d'envoyer la commande.", level: "danger" });
      return;
    }
    setBusy(busyKey);
    ws.send({ type: "host:command", hostId: hid, command });
    setStatus({ text: `${label} — appliqué au prochain heartbeat (≤30 s).`, level: "info" });
    window.setTimeout(() => {
      void loadPlugins(hid);
      setBusy(null);
    }, 800);
  }

  /**
   * Force le déchargement d'un plugin (ou tous) via plugin:unload.
   * Réponse synchrone (≤15s) — pas de passage par heartbeat.
   * Si holdup (HasPersistentState/jobs/sessions), propose un retry avec force.
   */
  function sendUnload(pluginId: string | null, label: string, opts: { all?: boolean; force?: boolean } = {}): void {
    if (!ws.isOpen()) {
      setStatus({ text: "WebSocket non connecté.", level: "danger" });
      return;
    }
    const requestId = `unload_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setStatus({ text: `Déchargement de ${label}…`, level: "info" });

    const off = ws.on(
      (m) => isWsEnvelope(m, "control", "plugin", "unload.result") && (m as { id?: string }).id === requestId,
      (m: unknown) => {
        off();
        const msg = ((m as { payload?: unknown }).payload ?? {}) as {
          ok?: boolean;
          result?: {
            unloaded?: string[];
            skipped?: Array<{ id: string; reason: string; count?: number }>;
            killed?: { jobs: number; sessions: number };
          };
          error?: string;
        };
        if (!msg.ok) {
          setStatus({ text: `Erreur unload : ${msg.error || ""}`, level: "danger" });
          return;
        }
        const r = msg.result || {};
        const ulist = r.unloaded || [];
        const slist = r.skipped || [];
        const killed = r.killed || { jobs: 0, sessions: 0 };

        if (ulist.length > 0) {
          const killTxt =
            killed.jobs + killed.sessions > 0 ? ` (FORCE: jobs=${killed.jobs} sessions=${killed.sessions} tués)` : "";
          setStatus({ text: `Déchargé ${ulist.length} plugin(s) : ${ulist.join(", ")}${killTxt}`, level: "info" });
        }
        if (slist.length > 0) {
          const reasons = slist.map((s) => `${s.id}: ${s.reason}${s.count ? ` (${s.count})` : ""}`).join(" · ");
          // Proposer force si bloqué par jobs/sessions/persistent
          const blocked = slist.filter((s) =>
            ["active_jobs", "open_ps_sessions", "persistent_state"].includes(s.reason),
          );
          if (blocked.length > 0 && !opts.force) {
            void (async () => {
              const ok = await confirmDialog.confirm({
                title: "Forcer le déchargement",
                body: `Plugin(s) bloqué(s) :\n${reasons}\n\nForcer le déchargement ? Cela tue les jobs/sessions actifs (irrécupérables).`,
                level: "danger",
                entityName: label,
                confirmLabel: "Forcer",
              });
              if (ok) {
                sendUnload(pluginId, label, { ...opts, force: true });
              } else {
                setStatus({ text: `Ignoré : ${reasons}`, level: ulist.length > 0 ? "info" : "danger" });
              }
            })();
            return;
          }
          setStatus({ text: `Ignoré : ${reasons}`, level: ulist.length > 0 ? "info" : "danger" });
        }
      },
    );

    ws.send(
      asWsOutgoing(
        envelope(
          "control",
          "plugin",
          "unload",
          { requestId, pluginId: opts.all ? null : pluginId, all: opts.all === true, force: opts.force === true },
          { id: requestId, deliveryClass: "must_deliver", dropPolicy: "never" },
        ),
      ),
    );

    // Garde-fou : si pas de réponse en 16s, off + erreur
    window.setTimeout(() => {
      off();
    }, 16_000);
  }

  onMount(() => {
    void loadHosts();
    ws.on(
      (m) => m.type === "error" && (m as { code?: string }).code === "host_command_failed",
      (m) => setStatus({ text: `Échec : ${(m as { message?: string }).message || ""}`, level: "danger" }),
    );
  });

  const toolbar = (
    <>
      <select
        class="panel-filter-input"
        value={hostId()}
        onChange={(e) => {
          setHostId(e.currentTarget.value);
          void loadPlugins(e.currentTarget.value);
        }}
      >
        <Show when={hosts().length} fallback={<option value="">Aucun hôte</option>}>
          <For each={hosts()}>{(h) => <option value={h.hostId}>{hostLabel(h)}</option>}</For>
        </Show>
      </select>
      <button
        type="button"
        class="btn btn--xs btn--primary"
        disabled={!hostId() || busy() === "__update"}
        onClick={() => sendCmd({ action: "update" }, "__update", "Mise à jour des composants")}
      >
        {busy() === "__update" ? "…" : "Mettre à jour"}
      </button>
      <button type="button" class="btn btn--xs btn--info" onClick={() => void loadPlugins(hostId())}>
        Rafraîchir
      </button>
      <button
        type="button"
        class="btn btn--xs"
        title="Décharge tous les plugins de la RAM du PluginHost (relazy-load au prochain tool)"
        onClick={() => sendUnload(null, "tous les plugins", { all: true })}
      >
        ⇣ Tout décharger
      </button>
    </>
  );

  return (
    <PanelLayout title="Plugins" toolbar={toolbar} status={status()}>
      <div class="plugins-panel">
        {/* ── Canal global du host ── */}
        <div class="plugins-group">
          <div class="plugins-group__title">Canal de mise à jour de l'hôte</div>
          <div class="plugin-row">
            <div class="plugin-row__main">
              <span class="plugin-row__name">Canal global</span>
              <span class="badge badge--muted">{hostChannel()}</span>
            </div>
            <select
              class="panel-filter-input"
              value={hostChannel()}
              disabled={!hostId() || busy() === "__channel"}
              onChange={(e) =>
                sendCmd(
                  { action: "channel", scope: "host", channel: e.currentTarget.value },
                  "__channel",
                  `Canal global → ${e.currentTarget.value}`,
                )
              }
            >
              <For each={CHANNELS}>{(c) => <option value={c}>{c}</option>}</For>
            </select>
          </div>
        </div>

        {/* ── Installés ── */}
        <div class="plugins-group">
          <div class="plugins-group__title">Plugins installés ({installed().length})</div>
          <Show
            when={installed().length}
            fallback={<div class="panel-data-empty">Aucun plugin assigné à cet hôte.</div>}
          >
            <For each={installed()}>
              {(p) => (
                <div class="plugin-row">
                  <div class="plugin-row__main">
                    <span class="plugin-row__name">{p.name}</span>
                    <span class="badge badge--ok">{p.installed_version || p.latest_stable || "—"}</span>
                    <Show when={!p.enabled}>
                      <span class="badge badge--muted">désactivé</span>
                    </Show>
                  </div>
                  <Show when={p.description}>
                    <div class="plugin-row__desc">{p.description}</div>
                  </Show>
                  {/* P4.2 — feedback file d'attente */}
                  <Show when={queued().has(p.id)}>
                    <div class="plugin-row__queued">⏳ En file d'attente — appliqué au prochain heartbeat (≤30 s)</div>
                  </Show>
                  <button
                    type="button"
                    class="btn btn--xs"
                    classList={{ "btn--info": openSettingsFor() === p.id }}
                    title="Réglages avancés : canal et isolation"
                    aria-expanded={openSettingsFor() === p.id}
                    onClick={() => setOpenSettingsFor(openSettingsFor() === p.id ? null : p.id)}
                  >
                    ⚙
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--info"
                    onClick={() => setOpenVersionsFor(openVersionsFor() === p.id ? null : p.id)}
                  >
                    ⓘ Versions
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs"
                    title="Décharge le plugin de la RAM du PluginHost (sera relazy-load au prochain tool)"
                    onClick={() => sendUnload(p.id, p.name)}
                  >
                    ⇣ Décharger
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--danger"
                    disabled={busy() === p.id}
                    onClick={() =>
                      sendCmd({ action: "plugin-uninstall", pluginId: p.id }, p.id, `Retrait de ${p.name}`)
                    }
                  >
                    {busy() === p.id ? "…" : "Retirer"}
                  </button>
                  {/* P4.1 — réglages avancés dépliables (canal + isolation) */}
                  <Show when={openSettingsFor() === p.id}>
                    <div class="plugin-row__settings">
                      <label class="plugin-setting">
                        <span class="plugin-setting__label">Canal</span>
                        <select
                          class="panel-filter-input"
                          value={p.channel || "stable"}
                          disabled={busy() === p.id}
                          onChange={(e) => {
                            markQueued(p.id);
                            sendCmd(
                              { action: "channel", scope: "plugin", pluginId: p.id, channel: e.currentTarget.value },
                              p.id,
                              `${p.name} → canal ${e.currentTarget.value}`,
                            );
                          }}
                        >
                          <For each={CHANNELS}>{(c) => <option value={c}>{c}</option>}</For>
                        </select>
                      </label>
                      <label class="plugin-setting">
                        <span class="plugin-setting__label">Isolation</span>
                        <select
                          class="panel-filter-input"
                          title="Isolation : auto = default manifest, shared = in-process, process = sub-process (kill propre)"
                          value={p.isolation || "auto"}
                          disabled={busy() === `${p.id}-iso`}
                          onChange={(e) => {
                            markQueued(p.id);
                            sendCmd(
                              { action: "isolation", pluginId: p.id, isolation: e.currentTarget.value },
                              `${p.id}-iso`,
                              `${p.name} → isolation ${e.currentTarget.value}`,
                            );
                          }}
                        >
                          <For each={ISOLATIONS}>{(i) => <option value={i}>{i}</option>}</For>
                        </select>
                      </label>
                    </div>
                  </Show>
                  <Show when={openVersionsFor() === p.id}>
                    <PluginVersionsPanel pluginId={p.id} onClose={() => setOpenVersionsFor(null)} />
                  </Show>
                </div>
              )}
            </For>
          </Show>
        </div>

        {/* ── Installables ── */}
        <div class="plugins-group">
          <div class="plugins-group__title">Plugins installables ({available().length})</div>
          <Show
            when={available().length}
            fallback={<div class="panel-data-empty">Tous les plugins du catalogue sont déjà assignés.</div>}
          >
            <For each={available()}>
              {(p) => (
                <div class="plugin-row">
                  <div class="plugin-row__main">
                    <span class="plugin-row__name">{p.name}</span>
                    <Show when={p.latest_stable}>
                      <span class="badge badge--muted">{p.latest_stable}</span>
                    </Show>
                  </div>
                  <Show when={p.description}>
                    <div class="plugin-row__desc">{p.description}</div>
                  </Show>
                  {/* P4.2 — feedback file d'attente */}
                  <Show when={queued().has(p.id)}>
                    <div class="plugin-row__queued">⏳ En file d'attente — appliqué au prochain heartbeat (≤30 s)</div>
                  </Show>
                  <button
                    type="button"
                    class="btn btn--xs btn--primary"
                    disabled={busy() === p.id}
                    onClick={() => {
                      markQueued(p.id);
                      sendCmd(
                        { action: "plugin-install", pluginId: p.id, channel: hostChannel() },
                        p.id,
                        `Installation de ${p.name}`,
                      );
                    }}
                  >
                    {busy() === p.id ? "…" : "Installer"}
                  </button>
                </div>
              )}
            </For>
          </Show>
        </div>
      </div>
    </PanelLayout>
  );
};

interface PluginVersion {
  id: string;
  version: string;
  channel: string;
  asset_sha256: string | null;
  published_at: string;
}

function PluginVersionsPanel(props: { pluginId: string; onClose: () => void }) {
  const [loading, setLoading] = createSignal(true);
  const [versions, setVersions] = createSignal<PluginVersion[]>([]);
  const [busy, setBusy] = createSignal<string | null>(null);
  const [msg, setMsg] = createSignal<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`/api/plugins/${encodeURIComponent(props.pluginId)}/versions`, {
        credentials: "include",
      });
      const data = (await r.json()) as { ok?: boolean; versions?: PluginVersion[] };
      if (data.ok) setVersions(data.versions || []);
    } catch {
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }

  async function promote(version: string, toChannel: string) {
    setBusy(`${version}→${toChannel}`);
    setMsg(null);
    try {
      const r = await fetch(
        `/api/plugins/${encodeURIComponent(props.pluginId)}/versions/${encodeURIComponent(version)}/promote`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ toChannel }),
        },
      );
      const data = (await r.json()) as { ok?: boolean; error?: string };
      if (data.ok) {
        setMsg(`✓ ${version} → ${toChannel}`);
        await load();
      } else {
        setMsg(`✗ ${data.error || "promotion échouée"}`);
      }
    } catch (e) {
      setMsg(`✗ ${String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  onMount(() => {
    void load();
  });

  return (
    <div
      style={{
        padding: "8px",
        border: "1px solid var(--border)",
        "border-radius": "4px",
        "margin-top": "8px",
        background: "var(--bg-2, #0f172a)",
      }}
    >
      <div style={{ display: "flex", "justify-content": "space-between", "align-items": "center" }}>
        <strong>Versions de {props.pluginId}</strong>
        <button class="btn btn--xs" type="button" onClick={() => props.onClose()}>
          Fermer
        </button>
      </div>
      <Show when={msg()}>
        <div style={{ "font-size": "11px", color: "#86efac", margin: "4px 0" }}>{msg()}</div>
      </Show>
      <Show when={loading()}>
        <div style={{ "font-size": "11px" }}>Chargement…</div>
      </Show>
      <Show when={!loading() && versions().length === 0}>
        <div style={{ "font-size": "11px", color: "var(--text-3)" }}>Aucune version publiée.</div>
      </Show>
      <For each={versions()}>
        {(v) => (
          <div
            style={{
              display: "flex",
              gap: "8px",
              "align-items": "center",
              padding: "4px 0",
              "border-bottom": "1px solid var(--border)",
              "flex-wrap": "wrap",
            }}
          >
            <span style={{ "font-family": "monospace", "min-width": "120px", "font-size": "12px" }}>{v.version}</span>
            <span style={{ "font-size": "11px", color: "var(--text-3)", "min-width": "60px" }}>{v.channel}</span>
            <span style={{ "font-size": "10px", color: "var(--text-3)", "font-family": "monospace" }}>
              {(v.asset_sha256 || "—").slice(0, 12)}
            </span>
            <button
              class="btn btn--xs"
              disabled={!!busy()}
              onClick={() => promote(v.version, "beta")}
              title="Promouvoir vers beta"
            >
              → beta
            </button>
            <button
              class="btn btn--xs"
              disabled={!!busy()}
              onClick={() => promote(v.version, "stable")}
              title="Promouvoir vers stable"
            >
              → stable
            </button>
          </div>
        )}
      </For>
    </div>
  );
}

registerPanel("plugins", PluginsPanel);
